import { createElement, useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { RefreshCw, Trash2, Zap } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/lib/auth/useAuth'
import { usePageLayout } from '@/hooks/usePageLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import SettingsSidebar from '@/components/app/sidebars/settings'
import { SettingsPageContainer } from '@/components/app/layout'
import {
  useCurrentUsage,
  usePlans,
  useSubscriptionInfo,
} from '@/lib/db/rxdb/hooks/entities/subscriptions'
import { useRxDB } from '@/lib/db/rxdb/hooks'
import { devSetPlan } from '@/lib/api/features/subscriptions/api'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

// Limits use -1 for "unlimited" (mirrors the backend seeder LimitsJson).
const formatTaskLimit = (tasks?: number) =>
  tasks === -1 ? 'Unlimited tasks' : `${tasks ?? 0} tasks`

const formatStorageLimit = (bytes?: number) => {
  if (bytes === -1) return 'Unlimited storage'
  if (!bytes) return '0MB storage'
  const gb = bytes / (1024 * 1024 * 1024)
  return gb >= 1
    ? `${gb}GB storage`
    : `${Math.round(bytes / (1024 * 1024))}MB storage`
}

export default function DevSubscriptionTestingPage() {
  usePageLayout({
    sidebarContent: createElement(SettingsSidebar),
    showHeader: false,
  })

  const { subscription, plan } = useSubscriptionInfo()
  const plans = usePlans()
  const usage = useCurrentUsage()
  const auth = useAuth()
  const { db } = useRxDB()
  const [isLoading, setIsLoading] = useState(false)
  const getErrorMessage = (err: any) =>
    err?.response?.data?.message ||
    err?.message ||
    (typeof err === 'string' ? err : '')

  const handleQuickSwitch = async (planCode: string) => {
    const selectedPlan = plans.find((p) => p.code === planCode)
    if (!selectedPlan) {
      toast.error('Plan not found')
      return
    }

    setIsLoading(true)

    try {
      const email = auth.user?.email || 'dev@test.com'

      await devSetPlan({
        planId: selectedPlan.id,
        planCode,
        billingInterval: 'monthly',
        billingEmail: email,
      })

      // Hard refresh to sync state - simpler for dev testing
      toast.success(`✅ Switched to ${selectedPlan.name}! Refreshing...`)
      setTimeout(() => window.location.reload(), 200)
    } catch (error: any) {
      setIsLoading(false)
      toast.error(getErrorMessage(error) || 'Failed to switch plan')
      logger.logError(
        error,
        LogCategory.SUBSCRIPTION,
        {
          component: 'DevSubscriptionTestingPage',
          action: 'quick_switch',
          planCode,
        }
      )
    }
  }

  const handleResetSubscription = async () => {
    if (!subscription) {
      toast.error('No subscription to reset')
      return
    }

    if (!confirm('Reset subscription? This will delete the subscription record.')) {
      return
    }

    setIsLoading(true)
    try {
      // Delete subscription from RxDB (will sync to backend)
      const doc = await db?.subscriptions.findOne(subscription.id).exec()
      if (doc) {
        await doc.remove()
        toast.success('Subscription reset!')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to reset')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetUsage = async () => {
    if (!usage) {
      toast.error('No usage data to reset')
      return
    }

    setIsLoading(true)
    try {
      // Reset usage to zero
      const doc = await db?.usage.findOne(usage.id).exec()
      if (doc) {
        await doc.update({
          $set: {
            usage: {},
            lastCalculated: Date.now(),
            updatedOn: Date.now(),
          },
        })
        toast.success('Usage metrics reset!')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to reset usage')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSetTrialDays = async (days: number) => {
    if (!subscription) {
      toast.error('No subscription found. Start a trial first.')
      return
    }

    setIsLoading(true)
    try {
      const doc = await db?.subscriptions.findOne(subscription.id).exec()
      if (doc) {
        const now = Date.now()
        const trialEnd = now + days * 24 * 60 * 60 * 1000

        await doc.update({
          $set: {
            trialEndDate: trialEnd,
            currentPeriodEnd: trialEnd,
            updatedOn: now,
          },
        })
        toast.success(`Trial extended to ${days} days!`)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to set trial days')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <SettingsPageContainer>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="rounded-lg bg-yellow-500/10 p-2">
              <Zap className="size-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                Dev Subscription Testing
              </h1>
              <Badge variant="outline" className="mt-1">
                Development Only
              </Badge>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Tools for testing subscription features and limitations
          </p>
        </div>

        <div className="space-y-6">
          {/* Current Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Current Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Plan:</span>
                  <span className="ml-2 font-medium">
                    {plan?.name || 'No subscription'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <span className="ml-2 font-medium">
                    {subscription?.status || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Plan Code:</span>
                  <span className="ml-2 font-mono text-xs">
                    {plan?.code || 'none'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Subscription ID:</span>
                  <span className="ml-2 font-mono text-xs truncate">
                    {subscription?.id || 'N/A'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Plan Switcher */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Plan Switcher</CardTitle>
              <CardDescription>
                Instantly switch between plans to test feature gates and limits
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {plans.map((p) => (
                  <Button
                    key={p.id}
                    variant={plan?.code === p.code ? 'default' : 'outline'}
                    onClick={() => handleQuickSwitch(p.code)}
                    disabled={isLoading}
                    className="flex-col h-auto py-4"
                  >
                    <span className="font-semibold">{p.name}</span>
                    <span className="text-xs opacity-80">
                      {formatTaskLimit(p.limits?.tasks)}
                    </span>
                    <span className="text-xs opacity-80">
                      {formatStorageLimit(p.limits?.storage)}
                    </span>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Trial Management */}
          {subscription?.status === 'trialing' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Trial Management</CardTitle>
                <CardDescription>
                  Adjust trial period for testing trial expiration warnings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSetTrialDays(14)}
                    disabled={isLoading}
                  >
                    14 days
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSetTrialDays(7)}
                    disabled={isLoading}
                  >
                    7 days
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSetTrialDays(3)}
                    disabled={isLoading}
                  >
                    3 days (warning)
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSetTrialDays(1)}
                    disabled={isLoading}
                  >
                    1 day (urgent)
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSetTrialDays(0)}
                    disabled={isLoading}
                  >
                    Expire now
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reset Actions */}
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="text-base text-destructive">
                Reset Actions
              </CardTitle>
              <CardDescription>
                Dangerous actions for testing - resets data locally
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleResetUsage}
                  disabled={isLoading || !usage}
                  className="border-yellow-500/50 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-500/10"
                >
                  <RefreshCw className="size-4" />
                  Reset Usage Metrics
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleResetSubscription}
                  disabled={isLoading || !subscription}
                  className="border-destructive/50 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="size-4" />
                  Delete Subscription
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                These changes are local and will sync to backend. Refresh page to see updates.
              </p>
            </CardContent>
          </Card>

          {/* Testing Checklist */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Testing Checklist</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-0.5" />
                  <span>
                    Switch to Free → Verify 100 task limit shows in usage metrics
                  </span>
                </label>
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-0.5" />
                  <span>
                    Switch to Individual or Team → Verify unlimited tasks shows
                  </span>
                </label>
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-0.5" />
                  <span>
                    Add feature gate to a component → Test with different plans
                  </span>
                </label>
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-0.5" />
                  <span>
                    Set trial to 3 days → Verify warning banner appears
                  </span>
                </label>
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-0.5" />
                  <span>
                    Set trial to 1 day → Verify urgent banner (red)
                  </span>
                </label>
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-0.5" />
                  <span>
                    Expire trial → Verify banner shows "ends today"
                  </span>
                </label>
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-0.5" />
                  <span>
                    Test upgrade prompts appear for gated features
                  </span>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Quick Links */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Links</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <a
                  href="/moly/settings/subscription"
                  className="block text-primary hover:underline"
                >
                  → Production Subscription Page
                </a>
                <a
                  href="https://github.com/your-repo/moly-references/blob/main/FEATURE_GATE_EXAMPLE.md"
                  className="block text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  → Feature Gate Documentation
                </a>
                <a
                  href="https://github.com/your-repo/moly-references/blob/main/STRIPE_INTEGRATION_REQUIREMENTS.md"
                  className="block text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  → Stripe Integration Guide
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
    </SettingsPageContainer>
  )
}

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/settings/dev-subscription',
)({
  beforeLoad: ({ params }) => {
    if (import.meta.env.PROD) {
      throw redirect({
        to: '/$workspaceSlug/settings/subscription',
        params,
      })
    }
  },
  component: DevSubscriptionTestingPage,
})
